<template>
  <Loading />
  <n-config-provider :theme="darkTheme" :theme-overrides="themeOverrides">
    <n-loading-bar-provider>
      <n-message-provider :placement="messagePlacement">
        <n-notification-provider>
          <n-modal-provider>
            <n-dialog-provider>
              <ToastInjector />
              <Sidebar :menu-options="menuOptions" :logo-src="logoSrc" title="ClaimsSnap">
                <n-layout id="default_layout_div" ref="DefaultLayoutDiv" :native-scrollbar="false">
                  <n-layout-header>
                    <Header :preview-user="previewUser" />
                  </n-layout-header>
                  <n-layout-content :native-scrollbar="false">
                    <main class="sr__main-body" :class="{ 'no-container': noContainer }" @click="collapseSidebar">
                      <div v-if="noContainer">
                        <CheckUserStatus />
                        <slot />
                      </div>
                      <div v-else>
                        <CheckUserStatus />
                        <div class="d-flex welcome">
                          <div>
                            <h2 v-if="displayBusinessName" class="text-white mb-1">
                              Welcome, {{ displayBusinessName }}
                            </h2>
                            <h2 v-else class="text-white mb-1">Welcome, {{ greetingName }}</h2>
                            <p class="text-gray">
                              <span>ClaimsSnap</span>
                              is fast, simple, and secure
                            </p>
                          </div>
                        </div>

                        <template v-if="$auth?.approved === false">
                          <div class="mt-4 text-left sr__not-approved">
                            <p>
                              Our team is reviewing your account for approval now. Hang in there, this shouldn't take
                              long.
                            </p>
                            <p>
                              Want to get approved faster? Email us at
                              <a :href="`mailto:support@snaprefund.io?subject=${supportMail}`" data-rel="external"
                                >Support@snaprefund.io</a
                              >
                            </p>
                          </div>
                        </template>

                        <div class="body-wrapper clearfix">
                          <div class="body-content">
                            <div class="row">
                              <slot />
                            </div>
                          </div>
                        </div>
                      </div>
                    </main>
                  </n-layout-content>
                  <n-layout-footer position="absolute" style="z-index: 990">
                    <div class="help-text">
                      <div v-if="!isDefaultLogo" class="powered-by">
                        Powered by
                        <a href="https://claimssnap.io" target="_blank" rel="noopener noreferrer">
                          <img :src="defaultLogoUrl" alt="ClaimsSnap" class="powered-by-logo" />
                        </a>
                      </div>
                    </div>

                    <Idle :timeout="idleTimeoutSeconds" sync-key="claimssnap-idle" style="display: none" />
                  </n-layout-footer>
                </n-layout>
              </Sidebar>
              <VersionWatermark />
              <VerifyPhoneToast
                :show="isVerifyPhoneToastVisible"
                @hide-toast="hideToast"
                @verify-click="onVerifyClick"
              />
            </n-dialog-provider>
          </n-modal-provider>
        </n-notification-provider>
      </n-message-provider>
    </n-loading-bar-provider>
  </n-config-provider>
</template>

<script setup lang="ts">
import { NImage, darkTheme } from 'naive-ui';
import type { MenuOption, NLayout } from 'naive-ui';
import { Settings } from '@vicons/tabler';
import { RouterLink } from 'vue-router';
import { getGlobalThemeOverrides } from '../../base/utils/theme-overrides';
import variables from '../../base/assets/scss/config/variables.module.scss';
import { DwollaCustomerType } from '../../base/common/enums/dwolla-customer-type';
import { useUserRolePermissionsStore } from '../../base/stores/user-role-permissions';
import { storeToRefs } from 'pinia';
import { AccountAppType } from '../../base/common/enums/account-app-type';
import { DwollaCustomerStatus } from '../../base/common/enums/dwolla-customer-status';
import fullName from '../../base/utils/full-name';
import { IDLE_TIMEOUT_DEFAULT_MINUTES } from '../../base/common/constants/idle-timeout';
import { isBrandingPreviewPath } from '../utils/branding-preview-channel';
import { useBrandingPreview } from '../composables/use-branding-preview';
const { BASE_URL } = useRuntimeConfig().public;

const { $isSendPaymentMenuVisible } = useNuxtApp();
const router = useRouter();
const route = useRoute();
const { data: $auth } = useAuth();
const profileStore = useProfileStore();
const userRoleStore = useUserRolePermissionsStore();
const { permissions } = storeToRefs(userRoleStore);
const { isVerifyPhoneToastVisible } = storeToRefs(profileStore);

const { sidebarCollapsedData: collapsed, setSidebarCollapsed } = useSidebarCollapse();

const { $sendButtonClickEvent, $sendEvent } = useNuxtApp();

const noContainer = computed(() => route.meta.noContainer === true);

/** `/pending-payment/branding-preview` embeds this layout inside the branding modals' iframe. */
const isBrandingPreview = isBrandingPreviewPath(route.path);

/** Branding preview only: values the modals are about to save, still absent from the session. */
const previewLogoUrl = ref<string | null>(null);
const previewBusinessName = ref<string | null>(null);

/**
 * Branding preview only: the merchant the embedding modal is signed in as. The frame shares the
 * host session, but the header prefers this so it cannot show a default avatar while its own
 * `profile-info` is still resolving. `null` outside the preview, which keeps the session values.
 */
const previewUser = ref<{ avatarUrl: string | null; userName: string | null; fullName: string | null } | null>(null);

const idleTimeoutSeconds = computed(() => ($auth.value?.idleTimeoutMinutes || IDLE_TIMEOUT_DEFAULT_MINUTES) * 60);

const greetingName = computed(
  () =>
    previewUser.value?.fullName ??
    fullName({
      firstName: $auth.value?.viewerFirstName ?? $auth.value?.firstName,
      lastName: $auth.value?.viewerLastName ?? $auth.value?.lastName,
    }),
);

/** Uses the modal's business name while the branding preview is running. */
const displayBusinessName = computed(() => previewBusinessName.value ?? $auth.value?.businessName);

watchEffect(async () => {
  const accountType = $auth.value?.accountAppType as AccountAppType;

  // The preview shares localStorage with the host tab: signing out from here would tear down the
  // very session the merchant is editing their branding in. Permissions are still loaded below so
  // the sidebar keeps rendering normally.
  if (accountType !== AccountAppType.ClaimSnap && accountType !== AccountAppType.DualApp) {
    if (isBrandingPreview) {
      return;
    }
    await profileStore.signOut();
  } else if (!userRoleStore.role) {
    const userRoles = localStorage.getItem('_user_roles');
    if (userRoles) {
      const app = localStorage.getItem('_origin_app');
      if (app) {
        userRoleStore.updateRoles(JSON.parse(userRoles));
        await userRoleStore.fetchPermissions(app);
      }
    }
  } else if (
    $auth.value?.verifiedWithDwolla === 'true' ||
    $auth.value?.verifiedWithDwolla === DwollaCustomerStatus.Suspended ||
    $auth.value?.verifiedWithDwolla === DwollaCustomerStatus.Deactivated ||
    $auth.value?.verifiedWithDwolla === DwollaCustomerStatus.Unverified ||
    $auth.value?.verifiedWithDwolla === DwollaCustomerStatus.Verified ||
    $auth.value?.type === DwollaCustomerType.Business ||
    $auth.value?.type === DwollaCustomerType.Personal
  ) {
    if (route.path === '/') navigateTo('/send-payment');
  } else if (
    $auth.value?.verifiedWithPlaid === 'true' ||
    $auth.value?.verifiedWithPlaid === 'verified' ||
    $auth.value?.verifiedWithPlaid === 'suspended' ||
    $auth.value?.verifiedWithPlaid === 'deactivated' ||
    $auth.value?.verifiedWithPlaid === 'unverified'
  ) {
    if (route.path === '/') navigateTo('/pending-payment');
  } else if (!isBrandingPreview) {
    await profileStore.signOut();
  }
});

let unsubscribeSendEventHook: () => void;

const supportMail = ref<string>('SnapRefund Support Request');
const DefaultLayoutDiv = ref<InstanceType<typeof NLayout> | null>(null);

const defaultLogoUrl = '/images/Logo-ClaimsSnap-400px.png';

const logoSrc = computed(() => {
  if (previewLogoUrl.value !== null) {
    return previewLogoUrl.value;
  }

  return $auth.value?.recipientBrandingSettings?.logoFileName
    ? `${BASE_URL}/image/${$auth.value?.recipientBrandingSettings?.logoFileName}`
    : defaultLogoUrl;
});

const previewColors = ref<{ primaryColor: string; secondaryColor: string } | null>(null);

const themeColors = computed(() => {
  const primaryColor = previewColors.value?.primaryColor ?? $auth.value?.recipientBrandingSettings?.primaryColor;
  const secondaryColor =
    (previewColors.value?.secondaryColor ?? $auth.value?.recipientBrandingSettings?.secondaryColor) ||
    variables.dsGreen;

  return {
    primaryColor: primaryColor ? `${primaryColor}bb` : variables.dsBlue,
    primaryColorHover: primaryColor || variables.dsBlueHover,
    secondaryColor,
    secondaryColorHover: secondaryColor,
  };
});

const themeOverrides = computed(() => getGlobalThemeOverrides(themeColors.value));

provide('themeColors', themeColors);

useBrandingPreview((state) => {
  previewLogoUrl.value = state.logoUrl;
  previewBusinessName.value = state.businessName;
  previewUser.value = {
    avatarUrl: state.userAvatarUrl,
    userName: state.userName,
    fullName: state.userFullName,
  };
  if (state.primaryColor && state.secondaryColor) {
    previewColors.value = { primaryColor: state.primaryColor, secondaryColor: state.secondaryColor };
  }
});

const isDefaultLogo = computed(() => {
  return logoSrc.value === defaultLogoUrl;
});

const isWalletAvailable = computed(() => {
  return $auth.value?.type === DwollaCustomerType.Business && $auth.value?.isWalletAvailable;
});

const isPaypalWalletEnabled = computed(() => {
  return $auth.value?.type === DwollaCustomerType.Business && $auth.value?.isPaypalWalletEnabled;
});

const isAnyWalletAvailable = computed(() => {
  return isWalletAvailable.value || isPaypalWalletEnabled.value;
});

const displayAgentSnap = computed(() => {
  return $auth.value?.originApp === 'agent';
});

const isSendPaymentMenuVisible = computed(() => {
  return $isSendPaymentMenuVisible($auth.value?.type);
});

const hideToast = () => {
  profileStore.$patch({ isVerifyPhoneToastVisible: false });
};

const onVerifyClick = () => {
  navigateTo({ name: 'my-preference' });
  hideToast();
};

const menuOptions = computed<MenuOption[]>(() => [
  {
    show: isSendPaymentMenuVisible.value && !displayAgentSnap.value && permissions.value.SEND_PAYMENTS_PAGE,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'send-payment',
          },
        },
        { default: () => 'Send Payment' },
      ),
    key: 'send-payment',
    icon: renderIcon(h(NImage, { width: 24, src: '/images/ionic-ios-send.svg' })),
  },
  {
    show: !displayAgentSnap.value && permissions.value.PENDING_PAYMENTS_PAGE,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'pending-payment',
          },
        },
        { default: () => 'Pending Payments' },
      ),
    key: 'pending-payment',
    icon: renderIcon(h(NImage, { width: 24, src: '/images/open-reload.svg' })),
  },
  {
    show: !displayAgentSnap.value && permissions.value.TRANSACTION_HISTORY_PAGE,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'transactions',
          },
        },
        { default: () => 'Transaction History' },
      ),
    key: 'transactions',
    icon: renderIcon(h(NImage, { width: 24, src: '/images/ionic-ios-paper.svg' })),
  },
  {
    show: permissions.value.BANKS_PAGE,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'bank',
          },
        },
        { default: () => 'Banks' },
      ),
    key: 'bank',
    icon: renderIcon(h(NImage, { width: 24, src: '/images/ionic-ios-card.svg' })),
  },
  {
    show: isAnyWalletAvailable.value && permissions.value.WALLETS_PAGE,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'wallet',
          },
        },
        { default: () => 'Wallet' },
      ),
    key: 'wallet',
    icon: renderIcon(h(NImage, { width: 24, src: '/images/wallet-icon.svg' })),
  },
  {
    show: permissions.value.PREFERENCES_PAGE,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'my-preference',
          },
        },
        { default: () => 'Preferences' },
      ),
    key: 'preferences',
    icon: renderIcon(Settings),
  },
  {
    show: permissions.value.TEAMS_PAGE && $auth.value?.type != DwollaCustomerType.ReceiveOnly,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'teams',
          },
        },
        { default: () => 'Teams' },
      ),
    key: 'teams',
    icon: renderIcon(h(NImage, { width: 24, src: '/images/teams.svg' })),
  },
  {
    show:
      !displayAgentSnap.value &&
      permissions.value.TRANSACTION_HISTORY_PAGE &&
      $auth.value?.type !== DwollaCustomerType.ReceiveOnly,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'check-intelligence',
          },
        },
        { default: () => 'Check Intelligence' },
      ),
    key: 'check-intelligence',
    icon: renderIcon(h(NImage, { width: 24, src: '/images/check-intelligence-icon.svg' })),
  },
  {
    show:
      !displayAgentSnap.value &&
      permissions.value.AI_ASSISTANT_PAGE &&
      $auth.value?.type !== DwollaCustomerType.ReceiveOnly,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'ai-assistant',
          },
        },
        { default: () => 'AiSnap' },
      ),
    key: 'ai-assistant',
    icon: renderIcon(h(NImage, { width: 24, src: '/images/brain-icon.svg' })),
  },
]);

const collapseSidebar = () => {
  if (window.innerWidth < 768 && !collapsed.value) {
    setSidebarCollapsed(true);
  }
};

onMounted(() => {
  if (DefaultLayoutDiv.value) {
    DefaultLayoutDiv.value.$el.addEventListener('click', async (event: MouseEvent) => {
      if (!(event.target instanceof HTMLElement)) {
        return;
      }
      if (
        event.target.nodeName === 'BUTTON' ||
        (event.target.nodeName === 'A' &&
          (event.target.getAttribute('href') === '#' || event.target.getAttribute('href') === 'javascript:void(0)'))
      ) {
        const buttonName =
          event.target.innerText.length > 0 ? event.target.innerText : (event.target.ariaLabel ?? 'Undefined button');
        await $sendButtonClickEvent(buttonName);
      }
    });

    unsubscribeSendEventHook = router.beforeEach((to, from) => {
      const eventData = {
        routeChangeTo: to.path,
        routeChangeFrom: from.path,
      };
      $sendEvent(eventData, EventTypes.AppRouteChanged);
    });
    profileStore.$patch({
      isVerifyPhoneToastVisible: router.currentRoute.value.name !== 'my-preference' && !$auth.value?.phoneVerified,
    });
  }
});

onUnmounted(() => {
  unsubscribeSendEventHook();
});
</script>

<style scoped lang="scss">
.column-flex-100h {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex-grow: 1;
}

.sr__main-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 24px;

  &.no-container {
    padding: 0;
  }
}
.help-text {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  padding: 24px;
  width: 100%;
  color: white;
  font-size: 15px;
  line-height: 20px;
  border-top: 1px solid #292336;

  .powered-by {
    margin-left: auto;
    font-size: 13px;
    opacity: 0.8;
    display: flex;
    align-items: center;

    .powered-by-logo {
      height: 35px;
      width: auto;
      margin-left: 8px;
    }
  }
}

@media (max-width: 767px) {
  .help-text {
    flex-direction: column;
    justify-content: center;

    .powered-by {
      margin-left: 0;
      margin-top: 10px;
    }
  }
}

.sr__not-approved {
  font-size: 24px;
  font-weight: bold;
}

.n-layout .n-layout-content {
  height: calc(100vh - 68px - 99px);
  padding: 0 !important;

  @media (min-width: 768px) {
    height: calc(100vh - 68px - 69px);
  }
}

#default_layout_div .n-layout-content {
  padding: 24px;

  &:has(.no-container) {
    padding: 0;
  }
}

.welcome {
  margin-bottom: 24px;

  p {
    margin: 0;
  }
}

.row {
  --bs-gutter-x: 0;
}
</style>
